import axios from 'axios'
import store from '../index'
import { make } from 'vuex-pathify'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'
import { formatDate } from '@/util/formatters'
import { formatToDateTime } from 'brazilian-values'
import { STATUS_POS_FATURAMENTO } from '@/enums/request'
import { CFOP_OPERATION_VENDA_REVENDA } from '@/enums/fiscal'

const getCurrentCompanyQuery = () => getQueryFromFilter({})

const refreshRequests = (filter) => Promise.all([
  store.dispatch('requestStore/get', filter),
  store.dispatch('requestStore/getSummary', filter),
])

const state = {
  request: {
    items: [],
    payments: [],
    buyOrderNfse: null,
    commitmentNumber: null,
    customerContactId: null,
    customerEquipmentId: null,
  },

  requests: {
    data: [],
    count: 0,
    limit: 50,
  },

  requestSummary: {
    total: 0,
    inProgress: 0,
    billed: 0,
    financialOpen: 0,
  },

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['createdAt'],
    sortDesc: [true],
    plate: null,
  },

  type: 'add',
  loading: false,
  dialog: false,
  dialogMovementStatus: false,
  dialogBill: false,
}

const mutations = make.mutations(state)

const actions = {
  add: ({ state }, data = null) => {
    const currentCompany = store.get('authStore@currentCompany')

    // adiciona o usuário principal da venda
    const requestUsers = [{
      id: store.get('authStore@currentUser').id,
      name: store.get('authStore@currentUser').name,
      comissionRate: store.get('authStore@currentUser').comissionRate,
      comissionType: store.get('authStore@currentUser').comissionType,
      comissionRateSecondary: store.get('authStore@currentUser').comissionRateSecondary,
      comissionTypeSecondary: store.get('authStore@currentUser').comissionTypeSecondary,
    }]

    // adiciona os usuários secundários
    currentCompany.users.forEach(u => {
      if (String(u) !== String(store.get('authStore@currentUser').id)) {
        return store.dispatch('userStore/getById', u)
          .then(res => {
            requestUsers.push({
              userId: res.data.id,
              name: res.data.name,
              comissionRate: res.data.comissionRate,
              comissionType: res.data.comissionType,
              comissionRateSecondary: res.data.comissionRateSecondary,
              comissionTypeSecondary: res.data.comissionTypeSecondary,
            })
          })
      }
    })

    state.request = {
      ...data,
      buyOrderNfse: data?.buyOrderNfse || null,
      commitmentNumber: data?.commitmentNumber || null,
      customerContactId: data?.customerContactId || null,
      customerEquipmentId: data?.customerEquipmentId || null,

      startedAt: formatToDateTime(new Date()),
      deliveryDate: formatToDateTime(new Date()),

      payments: [],
      items: [],
      users: requestUsers,
      companyObservation: currentCompany.requestObservation,
      fiscalOperationType: CFOP_OPERATION_VENDA_REVENDA,

      companyId: currentCompany.id,
    }
    state.type = 'add'
    state.dialog = true

    store.dispatch('userStore/get')
  },

  edit: ({ state }, { id, type = 'edit', crmEpoch = null }) => {
    state.loading = true
    return store.dispatch('requestStore/getById', id)
      .then(res => {
        if (crmEpoch !== null && crmEpoch !== store.state.crmOpportunityStore.epoch) {
          return
        }
        state.request = res.data

        state.type = type
        state.dialog = true

        // format data
        state.request.startedAt = formatToDateTime(new Date(res.data.startedAt))
        state.request.deliveryDate = formatDate(new Date(res.data.deliveryDate).toISOString().substr(0, 10))
        state.request.endedAt = res.data.endedAt ? formatToDateTime(new Date(res.data.endedAt)) : null
        state.request.firstDueDate = res.data.firstDueDate ? formatDate(new Date(res.data.firstDueDate).toISOString().substr(0, 10)) : undefined
        state.request.payments = state.request.payments
          ? state.request.payments.map(item => {
            item.dueDate = formatDate(new Date(item.dueDate).toISOString().substr(0, 10))
            item.paymentDate = item.paymentDate ? formatDate(new Date(item.paymentDate).toISOString().substr(0, 10)) : undefined

            item.dueMenu = false
            item.date = null

            return item
          })
          : []

        store.dispatch('userStore/get')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  openMovementStatusDialog: ({ state }, item) => {
    state.request = { ...item }
    state.dialogMovementStatus = true
  },

  save: ({ state, dispatch }, request) => {
    const id = request.id
    const method = id ? 'put' : 'post'

    state.loading = true
    return axios[method](`${process.env.VUE_APP_API_URL}/request/${id || ''}`, request)
      .then(async (res) => {
        state.dialog = false
        showSuccess('Pedido salvo com sucesso!')
        if (request.crmOpportunityId || request.crmOpportunityDraft) {
          await store.dispatch('crmOpportunityStore/get')
        }
        await dispatch('refresh')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  duplicate: ({ state }, id) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/request/${id}`)
      .then(res => {
        state.request = res.data

        delete state.request.id
        delete state.request.status
        delete state.request.createdAt
        delete state.request.deliveryDate
        delete state.request.justification

        state.type = 'add'
        state.dialog = true

        // format data
        state.request.firstDueDate = formatDate(new Date(res.data.firstDueDate).toISOString().substr(0, 10))
        state.request.payments = state.request.payments.map(item => {
          item.dueDate = formatDate(new Date(item.dueDate).toISOString().substr(0, 10))
          item.dueMenu = false
          item.date = null

          return item
        })

        store.dispatch('userStore/get')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  updateStatus: ({ state, dispatch }, request) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/request-update-status/${request.id}`, { status: request.status, justification: request.justification })
      .then(_ => {
        state.dialogMovementStatus = false
        showSuccess()
        return dispatch('refresh')
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  bill: async ({ state, dispatch }, ids) => {
    try {
      state.loading = true
      await axios.post(`${process.env.VUE_APP_API_URL}/request/billing`, { ids })
      state.dialogMovementStatus = false
      showSuccess()
      await dispatch('refresh')
    } catch (e) {
      showError(e)
    } finally {
      state.loading = false
    }
  },

  generateFinancial: async ({ state, dispatch }, payload) => {
    const requestId = payload?.id || payload?.request?.id || payload

    if (!requestId) {
      return showError('Pedido não encontrado')
    }

    try {
      state.loading = true
      await axios.post(`${process.env.VUE_APP_API_URL}/request/${requestId}/financial`)
      showSuccess('Financeiro gerado com sucesso!')
      await dispatch('refresh')
    } catch (e) {
      showError(e)
    } finally {
      state.loading = false
    }
  },

  block: async ({ state }, request) => {
    try {
      state.loading = true
      await axios.post(`${process.env.VUE_APP_API_URL}/request/block/${request.id}`, { justification: request.justification })
    } catch (e) {
      showError(e)
    } finally {
      state.loading = false
    }
  },

  export: ({ state }, id) => {
    state.loading = true
    store.dispatch('requestStore/generatePDF', { id })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  generatePDF: async (context, payload) => {
    const requestId = payload?.id || payload?.request?.id || payload

    if (!requestId) {
      return showError('Pedido não encontrado')
    }

    const response = await axios.get(`${process.env.VUE_APP_API_URL}/request/${requestId}/pdf?${getCurrentCompanyQuery()}`, {
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf',
      },
    })

    const contentType = String(response.headers?.['content-type'] || '').toLowerCase()
    if (contentType && !contentType.includes('application/pdf')) {
      throw new Error('O servidor não retornou um PDF válido')
    }

    const bytes = response.data instanceof ArrayBuffer
      ? response.data
      : response.data?.buffer || response.data
    const signature = new Uint8Array(bytes).slice(0, 4)
    const isPdfSignature = signature.length === 4 &&
      signature[0] === 0x25 &&
      signature[1] === 0x50 &&
      signature[2] === 0x44 &&
      signature[3] === 0x46

    if (!isPdfSignature) {
      throw new Error('O arquivo retornado não parece ser um PDF válido')
    }

    const blob = new Blob([bytes], { type: 'application/pdf' })
    const objectUrl = window.URL.createObjectURL(blob)
    window.open(objectUrl, '_blank')
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000)
  },

  sendPdfEmail: async ({ state }, requestId) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/request/${requestId}/email-pdf?${getCurrentCompanyQuery()}`)
      .then(() => showSuccess('Envio de PDF por email enfileirado com sucesso!'))
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  get: async ({ state }, filter = state.filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/request?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.requests = res.data

        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getSummary: async ({ state }, filter = state.filter) => {
    return axios.get(`${process.env.VUE_APP_API_URL}/request-summary?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.requestSummary = res.data
        return res.data
      })
      .catch(showError)
  },

  refresh: ({ state }, filter = state.filter) => refreshRequests(filter),

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/request/${id}`).catch(showError),

  requestReturn: ({ state, dispatch }, { requestId, items, justification }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/request/${requestId}/return?${getCurrentCompanyQuery()}`, { items, justification })
      .then(res => {
        showSuccess('Devolução fiscal enfileirada com sucesso!')
        dispatch('refresh')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  requestContingency: ({ state, dispatch }, { requestId, contingencyType, justification }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/request/${requestId}/fiscal-contingency?${getCurrentCompanyQuery()}`, { contingencyType, justification })
      .then(res => {
        showSuccess('Emissão em contingência enfileirada com sucesso!')
        dispatch('refresh')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  getFiscalOperations: (context, requestId) => axios.get(`${process.env.VUE_APP_API_URL}/request/${requestId}/fiscal-operations?${getCurrentCompanyQuery()}`)
    .then(res => res.data)
    .catch(showError),

  sendFiscalOperationEmail: ({ state }, operationId) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/operation/${operationId}/email?${getCurrentCompanyQuery()}`)
      .then(() => showSuccess('Envio de email enfileirado com sucesso!'))
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  generateFiscalOperationPDF: async ({ state }, operationId) => {
    state.loading = true
    try {
      const response = await axios.get(`${process.env.VUE_APP_API_URL}/fiscal/operation/${operationId}/pdf?${getCurrentCompanyQuery()}`, {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'application/pdf',
        },
      })

      const contentType = String(response.headers?.['content-type'] || '').toLowerCase()
      if (contentType && !contentType.includes('application/pdf')) {
        throw new Error('O servidor não retornou um PDF válido')
      }

      const bytes = response.data instanceof ArrayBuffer
        ? response.data
        : response.data?.buffer || response.data
      const signature = new Uint8Array(bytes).slice(0, 4)
      const isPdfSignature = signature.length === 4 &&
        signature[0] === 0x25 &&
        signature[1] === 0x50 &&
        signature[2] === 0x44 &&
        signature[3] === 0x46

      if (!isPdfSignature) {
        throw new Error('O arquivo retornado não parece ser um PDF válido')
      }

      const objectUrl = window.URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      window.open(objectUrl, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000)
    } catch (error) {
      showError(error)
    } finally {
      state.loading = false
    }
  },

  remove: ({ state }, id) => {
    state.loading = true
    return axios.delete(`${process.env.VUE_APP_API_URL}/request/${id}`)
      .then(_ => {
        showSuccess('Pedido excluído com sucesso!')
        return refreshRequests()
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
